import { cloneDeep } from 'lodash'
import i18n from '@/i18n'

import AbstractFilter from '@/classes/filters/AbstractFilter'

export default class AmountFilter extends AbstractFilter {
  /**
   * Create new amount filter
   *
   * @param {String} key
   * @param {String} label
   * @param {String} property
   * @param {Array} [value]
   */
  constructor (key, label, property, isExactAmount, parameters, value) {
    super(key, label, property, value || [])

    this.type = 'AmountFilter'

    const [minAmount = null, maxAmount = null] = value || []
    this._temporaryValue = { minAmount, maxAmount, isExactAmount: isExactAmount || false }

    this.options = { ...parameters }
  }

  /**
   * Get the applied value of this filter
   *
   * @return {Object}
   */
  get value () {
    return this._value
  }

  /**
   * Set the value of this filter
   *
   * @param {Object} value
   */
  set value (value) {
    this._value = value
    this._temporaryValue = cloneDeep(value)
  }

  /**
   * Get the exact amount
   *
   * @return {String}
   */
  get amount () {
    return this.hasValue() && this.value.isExactAmount ? this.value.minAmount : ''
  }

  /**
   * Set the exact amount
   *
   * @param {String} value
   */
  set amount (value) {
    this._value.isExactAmount = true
    this._temporaryValue.isExactAmount = true

    this._value.minAmount = value
    this._temporaryValue.minAmount = value
  }

  /**
   * Get min amount
   *
   * @return {String}
   */
  get minAmount () {
    return this.hasValue() && !this.value.isExactAmount ? this.value.minAmount : ''
  }

  /**
   * Set min amount
   *
   * @param {string} value
   */
  set minAmount (value) {
    this._value.minAmount = value
    this._temporaryValue.minAmount = value
  }

  /**
   * Get max amount
   *
   * @return {String}
   */
  get maxAmount () {
    return this.hasValue() && !this.value.isExactAmount ? this.value.maxAmount : ''
  }

  /**
   * Set max amount
   *
   * @param {string} value
   */
  set maxAmount (value) {
    this._value.maxAmount = value
    this._temporaryValue.maxAmount = value
  }

  /**
   * Get display value of this filter.
   *
   * @return {String}
   */
  get displayValue () {
    if (this.hasValue()) {
      return this.value.isExactAmount
        ? this.value.minAmount
        : this.value.minAmount && this.value.maxAmount
          ? `${this.value.minAmount} - ${this.value.maxAmount}`
          : this.value.minAmount
            ? `${i18n.t('label.min')}. ${this.value.minAmount}`
            : `${i18n.t('label.max')}. ${this.value.maxAmount}`
    }

    return ''
  }

  /**
   * Copy values from temporary value to value
   */
  apply () {
    this._value = (this._temporaryValue.minAmount || this._temporaryValue.maxAmount) ? cloneDeep(this._temporaryValue) : null
  }

  /**
   * Get value of this filter
   *
   * @return {String}
   */
  toString () {
    if (this.value && (this.value.minAmount || this.value.maxAmount)) {
      return this.value.isExactAmount
        ? `[eq]${this.value.minAmount}`
        : this.value.minAmount && this.value.maxAmount
          ? `[gte]${this.value.minAmount}[and][lte]${this.value.maxAmount}`
          : this.value.minAmount
            ? `[gte]${this.value.minAmount}`
            : `[lte]${this.value.maxAmount}`
    }

    return null
  }

  /**
   * Does this filter have a value?
   *
   * @return {boolean}
   */
  hasValue () {
    return this.value !== null && typeof this.value !== 'undefined' && (this.value.minAmount || this.value.maxAmount)
  }

  /**
   * Reset filter
   */
  reset () {
    this._value = null
    this._temporaryValue = { minAmount: null, maxAmount: null, isExactAmount: false }
  }

  /**
   * Cancel filter
   */
  cancel () {
    this._temporaryValue = this._value ? cloneDeep(this._value) : { minAmount: null, maxAmount: null, isExactAmount: false }
  }
}
